import { EventType } from "@/analytics/analytics-events";
import { StartEditWaypointsToolProperties } from "@/analytics/edit-waypoints-tool-properties";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { SceneContextMenuBase } from "@/components/ui/scene-context-menu";
import { ImageType } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useCanChangeTool } from "@/hooks/use-can-change-tool";
import { useCurrentArea } from "@/modes/mode-data-context";
import {
  enableLocationMarkerCreation,
  setLocationMarkerArea,
} from "@/store/location-marker-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { activateTool, ToolName } from "@/store/ui/ui-slice";
import { pickImageFiles } from "@/utils/pick-file";
import { roundedCalculatePosition } from "@faro-lotv/app-component-toolbox";
import {
  FaroMenuItem,
  FaroMenuSeparator,
  FlagSmallIcon,
  Img360SmallIcon,
  ImgSmallIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import { useCallback, useState, type JSX } from "react";
import { Vector3 } from "three";
import { createNewLocationMarker } from "../edit-waypoints/create-new-location-marker";
import { useEditWaypointsStore } from "../edit-waypoints/edit-waypoints-store";
import { useNewWaypointsStore } from "./new-waypoints/new-waypoints-store";

type SheetContextMenuProps = {
  /** The position of the context menu in three.js world coordinates */
  position: Vector3;

  /** The sheet to show the context menu for */
  sheetId: GUID;

  /** Callback when the menu should be closed */
  onClose(): void;
};

/**
 * @returns A context menu for quickly uploading data to a sheet
 */
export function SheetContextMenu({
  position,
  onClose,
}: SheetContextMenuProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();

  const { area } = useCurrentArea();

  const { addNewLocationMarker } = useEditWaypointsStore();

  const { addNewWaypoint } = useNewWaypointsStore();

  const onAddLocationMarker = useCallback(() => {
    try {
      Analytics.track<StartEditWaypointsToolProperties>(
        EventType.startEditWaypointsTool,
        {
          via: "sheet context menu",
        },
      );

      assert(area, "No area available to add the location marker to");

      dispatch(activateTool(ToolName.editWaypoints));
      dispatch(setLocationMarkerArea(area.id));
      dispatch(enableLocationMarkerCreation(true));

      addNewLocationMarker(createNewLocationMarker(position));

      onClose();
    } catch (error) {
      handleErrorWithToast({ title: "Could not add location marker", error });
    }
  }, [
    addNewLocationMarker,
    area,
    dispatch,
    handleErrorWithToast,
    onClose,
    position,
  ]);

  const onAddImg360 = useCallback(async () => {
    try {
      assert(area, "Area is required to upload 360 photo(s)");

      const files = await pickImageFiles();

      addNewWaypoint({
        id: generateGUID(),
        areaId: area.id,
        type: ImageType.Img360,
        name: files.length === 1 ? files[0].name : "New waypoint",
        worldPosition: position,
        files,
      });

      // The upload should happen in the background
      onClose();
    } catch (error) {
      handleErrorWithToast({ title: "Could not upload 360° photo(s)", error });
    }
  }, [addNewWaypoint, area, handleErrorWithToast, onClose, position]);

  const onAddImg2d = useCallback(async () => {
    try {
      assert(area, "Area is required to upload 2D photo(s)");

      const files = await pickImageFiles();

      addNewWaypoint({
        id: generateGUID(),
        areaId: area.id,
        type: ImageType.Img2D,
        name: files.length === 1 ? files[0].name : "New waypoint",
        worldPosition: position,
        files,
      });

      // The upload should happen in the background
      onClose();
    } catch (error) {
      handleErrorWithToast({ title: "Could not upload 2D photo(s)", error });
    }

    onClose();
  }, [addNewWaypoint, area, handleErrorWithToast, onClose, position]);

  const canChangeTool = useCanChangeTool();

  return (
    <AppAwareHtml
      position={position}
      calculatePosition={roundedCalculatePosition}
      style={{ pointerEvents: "none" }}
    >
      <div ref={setAnchorEl} />

      {anchorEl && (
        <SceneContextMenuBase
          aria-label="sheet context menu"
          anchorEl={anchorEl}
          onClose={onClose}
        >
          <FaroMenuItem
            Icon={FlagSmallIcon}
            label="Add location marker"
            secondaryText="Enables edit waypoints mode"
            onClick={onAddLocationMarker}
            dark
            disabled={!canChangeTool}
          />

          <FaroMenuSeparator dark />

          <FaroMenuItem
            Icon={Img360SmallIcon}
            label="Import 360° photos"
            onClick={onAddImg360}
            dark
          />

          <FaroMenuItem
            Icon={ImgSmallIcon}
            label="Import 2D photos"
            onClick={onAddImg2d}
            dark
          />
        </SceneContextMenuBase>
      )}
    </AppAwareHtml>
  );
}
